import { LngLatBounds, LngLatLike, Map } from "maplibre-gl";
import { useEffect } from "react";

export const toGeometryCollection = (lst: any[]): GeoJSON.FeatureCollection<GeoJSON.Geometry> =>
  lst && {
    type: "FeatureCollection",
    features: lst.map(({ geometry, ...properties }) => ({
      type: "Feature",
      properties,
      geometry,
    })),
  };

export const useSetLayoutProperty = (map: Map, layer: string, property: string, value: any) =>
  useEffect(() => {
    if (map?.getLayer(layer)) map.setLayoutProperty(layer, property, value);
  }, [map, layer, property, value]);

// Padding that keeps a target clear of the report panel on the left, and of a
// panel on the right when one is open (the workspace bar).
const panelPadding = (padding = 10, right = 0) => ({
  left: 80 + 432 + padding,
  top: padding,
  bottom: padding,
  right: 10 + right + padding,
});

export function fitBounds(map: Map, bounds: LngLatBounds, padding = 10, right = 0) {
  map.fitBounds(bounds, { padding: panelPadding(padding, right) });
}

// LngLatBounds of a geometry's exterior ring. Accepts a GeoJSON Feature or a
// bare Polygon/MultiPolygon geometry; returns null if no ring can be found.
export function geometryBounds(geometry): LngLatBounds | null {
  const geom = geometry?.type === "Feature" ? geometry.geometry : geometry;
  const coords = geom?.coordinates;
  const ring = geom?.type === "MultiPolygon" ? coords?.[0]?.[0] : coords?.[0];
  if (!ring?.length) return null;
  const bounds = new LngLatBounds();
  for (const c of ring) bounds.extend(c);
  return bounds;
}

// Recenter on a point at the current zoom, keeping it clear of the report panel.
export function panTo(map: Map, center: LngLatLike, padding = 10) {
  map.easeTo({ center, padding: panelPadding(padding) });
}

export function zoomTo(map: Map, geometry, padding = 10) {
  const bounds = new LngLatBounds();
  const polygon = geometry?.coordinates || geometry;
  if (!polygon?.[0]) return console.error("zoomTo: no polygon", polygon);
  for (const c of polygon[0]) bounds.extend(c);
  fitBounds(map, bounds, padding);
}

export function boundsForList(lst: any[]) {
  const bounds = new LngLatBounds();
  lst.forEach((parcel) => {
    const geometry = parcel.geometry;
    const polygon = geometry?.coordinates || geometry;
    if (polygon?.[0]) {
      for (const c of polygon[0]) bounds.extend(c);
    }
  });
  return bounds;
}

// Niet verspringen als de percelen al goed in beeld staan: helemaal binnen de
// kaart, en niet als een stipje. Eerst telde alleen het midden, en dan bleef
// de kaart hangen op een uitsnede van het project.
export function alreadyInView(mapBounds: LngLatBounds, bounds: LngLatBounds) {
  if (!mapBounds.contains(bounds.getSouthWest()) || !mapBounds.contains(bounds.getNorthEast()))
    return false;
  const mArea = Math.abs(
    (mapBounds.getEast() - mapBounds.getWest()) * (mapBounds.getNorth() - mapBounds.getSouth())
  );
  const area = Math.abs(
    (bounds.getEast() - bounds.getWest()) * (bounds.getNorth() - bounds.getSouth())
  );
  return area / mArea > 0.04;
}

export function fitBoundsIfRelevant(map: Map, bounds: LngLatBounds) {
  if (bounds.isEmpty() || alreadyInView(map.getBounds(), bounds)) return;
  fitBounds(map, bounds);
}
